import * as React from "react";
import ReactDOM from "react-dom";
import { MenuItem, MenuItemState, Toast, ComboBox } from "@skbkontur/react-ui";
import { MenuMessage } from "@skbkontur/react-ui/internal/MenuMessage";
import { BookClosedIcon } from "@skbkontur/icons";

import { iterateEntries } from "../../../../../Common/IterableUtils";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { emptyRef } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ExternalBindingPath } from "../../../Engine/Core/CoreTypes";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { isDOMRectInView, tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";
import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";
import { pluralize } from "../../../Helpers/StringHelpers";
import { IForm } from "../../../Engine/Form/IForm";
import { reject } from "../../../../../Common/TypingUtils";
import { Contractor, ServiceContractor } from "../../../ContractorsServiceIntegration/Types/ContractorsTypes";
import { buildFullName, buildInnKpp } from "../../../Helpers/RequisitesHelpers";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { CONTRACTORS_SUGGEST_COMBOBOX_OPEN_SIDE_PAGE, CONTRACTORS_SUGGEST_COMBOBOX_SELECT } from "../../metricsAction";
import { TextOverflowHint } from "../../Internals/TextOverflowHint/TextOverflowHint";
import { AddButtonWrapper, ReadonlyValueWrapper } from "../ComboBox/ComboBox.styles";
import { Text } from "../../ValueViewers/Text/Text";

import { selectSearchText } from "./CustomSuggestComboBox";
import * as styles from "./CustomSuggestComboBox.styles";

export interface ContractorsSuggestComboBoxProps extends ControlValidationProps, ControlTooltipProps {
    form: IForm;
    bindingPath: AbsoluteModelPath;
    value: ContractorComboboxItem | undefined;
    onChange: (value: string | undefined) => void;
    allowTextWrap?: boolean;
    onBlur?: () => void;
    onFocus?: () => void;
    onMouseEnter?: (event: React.SyntheticEvent) => void;
    onMouseLeave?: (event: React.SyntheticEvent) => void;
    placeholder?: string;
    setInfo: (path: ExternalBindingPath, payload: string) => void;
    width?: string | number;
    disabled?: boolean;
    onQuery: (search: string | null) => Promise<ContractorsQueryResult | null>;
    onUnexpectedInput: (query: string) => void;
    isReadOnly?: boolean;
}

export interface ContractorsQueryResult {
    items?: ContractorComboboxItem[];
    totalCount?: number;
    setContractorToInner: (contractor: Contractor) => void;
}

export type ContractorComboboxItem = {
    displayValue?: string;
    payload?: { [key: string]: string };
} & Partial<ServiceContractor>;

type ItemArrayType = ContractorComboboxItem | React.ReactElement;

export const ContractorsSuggestComboBox = (props: ContractorsSuggestComboBoxProps) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isWidgetLoading, setIsWidgetLoading] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [totalCount, setTotalCount] = React.useState(0);
    const contractorToInnerCallbackRef = React.useRef<ContractorsQueryResult["setContractorToInner"]>();
    const rootRef = React.useRef<HTMLDivElement>(emptyRef);
    const comboboxRef = React.useRef<ComboBox<ContractorComboboxItem>>(emptyRef);
    const toastRef = React.useRef<Toast>(emptyRef);
    const track = useMetrics();

    const tooltipTrigger = props.error && (isFocused || isHovered) ? "opened" : "closed";
    const tooltipPosition = props.tooltipPosition ?? "top left";

    const importFromContractorsWidget = (setContractorToInner: (contractor: Contractor) => void) => async () => {
        track(CONTRACTORS_SUGGEST_COMBOBOX_OPEN_SIDE_PAGE);

        setIsWidgetLoading(true);
        const contractors = await props.form.importContractorsWidgetOptions({
            directoryId: props.form.contractorsClient.getDirectoryId() ?? reject("directoryId not found"),
            mode: "single",
        });
        setIsWidgetLoading(false);

        if (contractors && contractors[0]) {
            setContractorToInner(contractors[0].contractorDetails);
        }
    };

    const valueToString = (value: ContractorComboboxItem): string =>
        value.displayValue ?? value.organization?.inn ?? value.entrepreneur?.inn ?? "";

    const handleChange = (value: ContractorComboboxItem) => {
        track(CONTRACTORS_SUGGEST_COMBOBOX_SELECT);

        if (value.payload) {
            for (const [path, infoValue] of iterateEntries(value.payload)) {
                props.setInfo(path, infoValue);
            }
        } else {
            props.onChange(value.displayValue);
        }
    };

    const handleUnexpectedInput = (query: string): ContractorComboboxItem | undefined | null => {
        props.onUnexpectedInput(query);

        return {
            displayValue: query,
        };
    };

    const handleFocus = () => {
        setIsFocused(true);
        props.onFocus?.();
    };

    const handleBlur = () => {
        setIsFocused(false);
        props.onBlur?.();
    };

    const handleMouseEnter = (event: React.SyntheticEvent) => {
        setIsHovered(true);
        props.onMouseEnter?.(event);
    };

    const handleMouseLeave = (event: React.SyntheticEvent) => {
        setIsHovered(false);
        props.onMouseLeave?.(event);
    };

    const handleRequestFocus = (): void => {
        if (rootRef.current != undefined) {
            tryScrollIntoViewWithFallback(rootRef.current, { block: "center", inline: "center" });
        }

        comboboxRef.current?.focus();
    };

    const getEmptyMessage = () =>
        !!search
            ? "Контрагент не найден. Поищите по другим словам или создайте нового в справочнике"
            : "Контрагенты не найдены. Укажите нужные данные или создайте запись в справочнике";

    const getItems = async (query: string): Promise<ItemArrayType[]> => {
        const getItemsQuery = query ? query : props.value?.displayValue;

        const data = await props.onQuery(getItemsQuery ?? null);
        if (!data) {
            toastRef.current?.push("Не удалось загрузить контрагентов");
            throw new Error("Не удалось загрузить контрагентов");
        }
        contractorToInnerCallbackRef.current = data?.setContractorToInner;

        setTotalCount(data.totalCount ?? 0);

        const result: ItemArrayType[] =
            data?.items && data.items.length > 0
                ? data.items
                : [
                      <MenuMessage key={"empty"}>
                          <div css={styles.total}>{getEmptyMessage()}</div>
                      </MenuMessage>,
                  ];

        return result;
    };

    const renderTotalCount = (found: number, total: number) => (
        <div css={styles.total}>
            {`${pluralize(found, ["Показан", "Показано", "Показано"], undefined, true)} ${found} из ${total}, уточните запрос, чтобы найти нужного контрагента`}
        </div>
    );

    const renderItem = (value: ContractorComboboxItem, state?: MenuItemState): React.ReactNode => {
        let title = "";
        let requisites = "";
        if (value.organization) {
            title = value.organization.shortName ?? "";
            requisites = buildInnKpp(value.organization.inn, value.organization.kpp);
        }
        if (value.entrepreneur && value.entrepreneur.fio) {
            title = buildFullName(
                value.entrepreneur.fio.lastName ?? "",
                value.entrepreneur.fio.firstName ?? "",
                value.entrepreneur.fio.middleName
            );
            requisites = value.entrepreneur.inn;
        }

        return (
            <div key={title + requisites} css={styles.item} className={!props.allowTextWrap ? "no-wrap" : undefined}>
                <div>{selectSearchText(title, search)}</div>
                <div css={styles.hint} className={state === "hover" ? "hover" : undefined}>
                    {selectSearchText(requisites, search)}
                </div>
            </div>
        );
    };

    const renderAddButton = () =>
        contractorToInnerCallbackRef.current ? (
            <AddButtonWrapper key={"add-button"}>
                <MenuItem
                    disabled={isWidgetLoading}
                    icon={<BookClosedIcon />}
                    onClick={importFromContractorsWidget(contractorToInnerCallbackRef.current)}>
                    Открыть справочник контрагентов
                </MenuItem>
            </AddButtonWrapper>
        ) : undefined;

    if (props.isReadOnly) {
        return (
            <TextOverflowHint text={props.value?.displayValue ?? ""} width={20}>
                <ReadonlyValueWrapper>
                    <Text bindingPath={props.bindingPath} value={props.value?.displayValue} />
                </ReadonlyValueWrapper>
            </TextOverflowHint>
        );
    }

    return (
        <TestPageObjectHandle
            id={props.bindingPath}
            pageObject={{
                getType: () => "ContractorsSuggestComboBox",
                isFocused: () =>
                    document.activeElement === toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.querySelector("input"),
                isInView: () =>
                    isDOMRectInView(toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.getBoundingClientRect()),
                getValue: () => props.value,
                setValue: (nextValue: string = "") => {
                    props.onChange(nextValue);
                },
            }}>
            <FocusManagementObserver onRequestFocus={handleRequestFocus}>
                <Tooltip pos={tooltipPosition} trigger={tooltipTrigger} value={props.error} width={props.tooltipWidth}>
                    <div ref={rootRef}>
                        <ComboBox<ContractorComboboxItem>
                            ref={comboboxRef}
                            getItems={getItems}
                            value={props.value}
                            error={Boolean(props.error)}
                            placeholder={props.placeholder}
                            drawArrow={false}
                            itemToValue={valueToString}
                            valueToString={valueToString}
                            renderItem={renderItem}
                            renderValue={valueToString}
                            onValueChange={handleChange}
                            onUnexpectedInput={handleUnexpectedInput}
                            disabled={props.disabled}
                            width={props.width}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            onInputValueChange={setSearch}
                            renderAddButton={renderAddButton}
                            totalCount={totalCount}
                            renderTotalCount={renderTotalCount}
                        />
                    </div>
                </Tooltip>
            </FocusManagementObserver>
            <Toast ref={toastRef} />
        </TestPageObjectHandle>
    );
};
